import { getHttpClient } from './httpClient';

// ============================================================================
// Types — alignes sur le backend (cf modules/billing/services/contractService.ts)
// ============================================================================

export type BillingMode = 'stripe' | 'contract';

export type ContractAuditEventType =
  | 'contract.created'
  | 'contract.seats_changed'
  | 'contract.plan_seats_changed'
  | 'contract.renewed'
  | 'contract.terms_changed'
  | 'contract.reverted_to_stripe';

export interface ContractSubscription {
  id: string;
  enterprise_id: string;
  plan_tier_id: string;
  status: string;
  billing_mode: BillingMode;
  team_quantity: number;
  contract_reference: string | null;
  contract_signed_at: string | null;
  contract_starts_at: string | null;
  contract_ends_at: string | null;
  contract_notes: string | null;
  last_quota_alert_threshold: number | null;
  plan_slug?: string;
  plan_name?: string;
  created_at: string;
  updated_at: string;
}

export interface ContractUsage {
  current: number;
  quota: number;
  usage_pct: number;
}

export interface ContractPlanDetail {
  planTierId: string;
  planSlug: string | null;
  planName: string | null;
  seatsPurchased: number;
  seatsUsed: number;
  seatsRemaining: number;
}

export interface ContractDetails {
  subscription: ContractSubscription;
  usage: ContractUsage;
  /**
   * Multi-plan (PR3) : liste détaillée des plans achetés avec leur compteur
   * d'utilisateurs actifs affectés. Source pour le tableau plans côté UI.
   */
  plans: ContractPlanDetail[];
}

export interface ContractAuditEvent {
  id: string;
  subscription_id: string;
  enterprise_id: string;
  event_type: ContractAuditEventType;
  acting_admin_user_id: string | null;
  acting_admin_email: string | null;
  before_state: Record<string, unknown> | null;
  after_state: Record<string, unknown> | null;
  reason: string | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

export interface CreateContractPayload {
  // Mono-plan legacy : planSlug + teamQuantity au top-level.
  planSlug?: string;
  teamQuantity?: number;
  // Multi-plan : tableau de plans. Si fourni et non vide, source de vérité
  // (le premier devient le primary plan côté legacy fields).
  plans?: ContractPlanInput[];
  contractReference: string;
  contractSignedAt: string;
  contractStartsAt?: string | null;
  contractEndsAt: string;
  contractNotes?: string | null;
  reason: string;
}

export interface UpdateContractPayload {
  teamQuantity?: number;
  contractReference?: string;
  contractSignedAt?: string;
  contractStartsAt?: string | null;
  contractEndsAt?: string;
  contractNotes?: string | null;
  planTierId?: string;
  reason: string;
}

// ============================================================================
// API calls
// ============================================================================

export async function getContract(enterpriseId: string): Promise<ContractDetails | null> {
  const { data } = await getHttpClient().get<ContractDetails | null>(
    `/api/admin/enterprises/${enterpriseId}/contract`,
  );
  return data;
}

export async function createContract(
  enterpriseId: string,
  payload: CreateContractPayload,
): Promise<{ ok: boolean; subscription: ContractSubscription }> {
  const { data } = await getHttpClient().post(
    `/api/admin/enterprises/${enterpriseId}/contract`,
    payload,
  );
  return data;
}

export async function updateContract(
  enterpriseId: string,
  payload: UpdateContractPayload,
): Promise<{ ok: boolean; subscription: ContractSubscription }> {
  const { data } = await getHttpClient().patch(
    `/api/admin/enterprises/${enterpriseId}/contract`,
    payload,
  );
  return data;
}

export async function revertContractToStripe(
  enterpriseId: string,
  reason: string,
): Promise<{ ok: boolean; subscription: ContractSubscription }> {
  const { data } = await getHttpClient().post(
    `/api/admin/enterprises/${enterpriseId}/contract/revert`,
    { reason },
  );
  return data;
}

export async function getContractAudit(
  enterpriseId: string,
  limit = 100,
): Promise<{ events: ContractAuditEvent[] }> {
  const { data } = await getHttpClient().get(
    `/api/admin/enterprises/${enterpriseId}/contract/audit?limit=${limit}`,
  );
  return data;
}

// ============================================================================
// Multi-plan PR3 — PATCH contract/plans + downgrade workflow
// ============================================================================

export interface ContractPlanInput {
  planSlug: string;
  teamQuantity: number;
}

export interface UpdateContractPlansPayload {
  plans: ContractPlanInput[];
  contractReference?: string;
  contractSignedAt?: string;
  contractStartsAt?: string | null;
  contractEndsAt?: string;
  contractNotes?: string | null;
  usersToDeactivate?: string[];
  reason: string;
}

export interface ActiveUserOnPlanSummary {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface OverflowPlan {
  planSlug: string;
  planName: string;
  planTierId: string;
  requestedQuota: number;
  activeUsers: ActiveUserOnPlanSummary[];
}

/**
 * Erreur 409 PRE_DOWNGRADE_DEACTIVATION_REQUIRED retournée quand un quota
 * cible est inférieur aux users actifs sur ce plan. Le frontend ouvre une
 * modale pour sélectionner les users à désactiver, puis re-soumet avec
 * usersToDeactivate[].
 */
export interface PreDowngradeDeactivationRequired {
  code: 'PRE_DOWNGRADE_DEACTIVATION_REQUIRED';
  overflowPlans?: OverflowPlan[];
  removedPlanId?: string;
  activeUsers?: ActiveUserOnPlanSummary[];
}

export async function updateContractPlans(
  enterpriseId: string,
  payload: UpdateContractPlansPayload,
): Promise<{ ok: boolean; subscription: ContractSubscription; deactivatedCount: number }> {
  const { data } = await getHttpClient().patch(
    `/api/admin/enterprises/${enterpriseId}/contract/plans`,
    payload,
  );
  return data;
}
